import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Search, RefreshCw, Filter, Package, CheckCircle2,
  Loader2, AlertCircle, Eye, X, BarChart3, Calendar, TrendingDown,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORMS = [
  { key: "emag_ro_bau", label: "BAU · RO", group: "BAU", color: "sky" },
  { key: "emag_bg_bau", label: "BAU · BG", group: "BAU", color: "sky" },
  { key: "emag_hu_bau", label: "BAU · HU", group: "BAU", color: "sky" },
  { key: "emag_ro_fbe", label: "FBE · RO", group: "FBE", color: "emerald" },
  { key: "emag_bg_fbe", label: "FBE · BG", group: "FBE", color: "emerald" },
  { key: "emag_hu_fbe", label: "FBE · HU", group: "FBE", color: "emerald" },
];

// Status badge colors match the eMAG seller dashboard pill style (solid fill,
// white text). `dot` is kept for backwards compatibility but unused now.
const STATUSES = {
  1: { label: "Incomplet",   dot: "bg-zinc-400",    cls: "bg-zinc-500 text-white border-zinc-500" },
  2: { label: "Nou",         dot: "bg-orange-500",  cls: "bg-orange-500 text-white border-orange-500" },
  3: { label: "Preluat",     dot: "bg-sky-500",     cls: "bg-sky-500 text-white border-sky-500" },
  4: { label: "Respins",     dot: "bg-rose-500",    cls: "bg-rose-500 text-white border-rose-500" },
  5: { label: "Anulat",      dot: "bg-zinc-900",    cls: "bg-zinc-900 text-white border-zinc-700" },
  6: { label: "Recepționat", dot: "bg-violet-500",  cls: "bg-violet-500 text-white border-violet-500" },
  7: { label: "Finalizat",   dot: "bg-emerald-500", cls: "bg-emerald-500 text-white border-emerald-500" },
};

// Allowed transitions per eMAG state-machine (mirrors backend).
const STATUS_TRANSITIONS = {
  2: [3, 4],
  3: [4, 5, 6],
  6: [4, 7],
};

const PICKUP_METHODS = {
  1: "Curier eMAG", 2: "Curier propriu", 3: "Trimitere de client",
  4: "În locker (EasyBox)", 5: "Returnare la magazin",
};

const RETURN_TYPES = {
  1: "Înlocuire (același produs)", 2: "Înlocuire (alt produs)",
  3: "Restituire", 4: "Anulare contract", 5: "Voucher",
};

export default function EmagRmas() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {}, by_platform: {} });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 50;

  // filters
  const [selectedPlatforms, setSelectedPlatforms] = useState(new Set());
  const [statusFilter, setStatusFilter] = useState("");
  // Prefill from URL query params (used by Dashboard "Vezi returul" deep-link).
  const _initialQuery = (() => {
    if (typeof window === "undefined") return "";
    const p = new URLSearchParams(window.location.search);
    return (p.get("rma_id") || p.get("q") || "").trim();
  })();
  const [query, setQuery] = useState(_initialQuery);
  const [openRma, setOpenRma] = useState(null);
  const [counters, setCounters] = useState({ yesterday: 0, today: 0, prev_month: 0, cur_month: 0, orders_yesterday: 0, orders_today: 0, orders_prev_month: 0, orders_cur_month: 0 });
  const [productReportOpen, setProductReportOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), page_size: PAGE_SIZE.toString() });
      if (selectedPlatforms.size) params.set("platform", [...selectedPlatforms].join(","));
      if (statusFilter) params.set("status", statusFilter);
      if (query.trim()) params.set("q", query.trim());
      const r = await axios.get(`${API}/emag-rmas?${params}`);
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setStats(r.data.stats || { total: 0, by_status: {}, by_platform: {} });
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [page, selectedPlatforms, statusFilter, query]);

  useEffect(() => { load(); }, [load]);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/emag-rmas/sync-now`);
      const t = r.data?.total || {};
      toast.success(`Sync OK · +${t.new || 0} noi · ${t.updated || 0} actualizate · ${t.errors || 0} erori`);
      await load();
      loadCounters();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSyncing(false);
    }
  };

  const loadCounters = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/emag-rmas/stats/counters`);
      setCounters(r.data || {});
    } catch { /* silent */ }
  }, []);
  useEffect(() => { loadCounters(); }, [loadCounters]);

  const sendDigest = async (kind) => {
    const label = kind === "daily" ? "digest zilnic (retururile de ieri)" : "digest lunar (luna trecută)";
    if (!window.confirm(`Trimit ${label} pe email acum?`)) return;
    try {
      const r = await axios.post(`${API}/emag-rmas/digest/send-${kind}`, {});
      const cnt = r.data?.counts || 0;
      toast.success(`${label}: ${cnt} retururi · trimis pe email`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const togglePlatform = (k) => {
    setPage(1);
    setSelectedPlatforms((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };
  const selectGroup = (group) => {
    setPage(1);
    const groupKeys = PLATFORMS.filter((p) => p.group === group).map((p) => p.key);
    const allOn = groupKeys.every((k) => selectedPlatforms.has(k));
    setSelectedPlatforms((prev) => {
      const next = new Set(prev);
      groupKeys.forEach((k) => { allOn ? next.delete(k) : next.add(k); });
      return next;
    });
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const statusCards = useMemo(() => Object.entries(STATUSES).map(([k, def]) => ({
    k, ...def, n: stats.by_status[k] || 0,
  })), [stats]);

  return (
    <div className="space-y-6 p-6">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 flex items-center gap-2">
            <Package className="h-6 w-6 text-emerald-400" /> Retururi eMAG (RMA)
          </h1>
          <p className="text-sm text-zinc-400 mt-1">
            Cereri de retur primite de la cumpărători, sincronizate automat la 10 min din toate cele 6 conturi (BAU + FBE × RO/BG/HU).
          </p>
        </div>
        <button data-testid="emag-rmas-sync"
          onClick={syncNow} disabled={syncing}
          className="inline-flex items-center gap-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-4 py-2 text-sm font-medium text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-50">
          {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Sincronizează acum
        </button>
      </header>

      {/* Counters + digest actions — retururi înregistrate (după `date` eMAG).
          Ignoră status-ul. „Ieri" / „Luna trecută" reflectă intervalele
          calendaristice complete în Europe/Bucharest. */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { key: "yesterday", label: "Retururi ieri", value: counters.yesterday, orders: counters.orders_yesterday, color: "orange" },
          { key: "today", label: "Retururi azi", value: counters.today, orders: counters.orders_today, color: "sky" },
          { key: "prev_month", label: "Retururi luna trecută", value: counters.prev_month, orders: counters.orders_prev_month, color: "violet" },
          { key: "cur_month", label: "Retururi luna curentă", value: counters.cur_month, orders: counters.orders_cur_month, color: "emerald" },
        ].map((b) => {
          const pct = b.orders > 0 ? ((b.value / b.orders) * 100) : null;
          return (
          <div key={b.key} data-testid={`emag-rma-counter-${b.key}`}
            className={`rounded-2xl border p-4 ${
              b.color === "orange" ? "border-orange-500/30 bg-orange-500/5" :
              b.color === "sky" ? "border-sky-500/30 bg-sky-500/5" :
              b.color === "violet" ? "border-violet-500/30 bg-violet-500/5" :
              "border-emerald-500/30 bg-emerald-500/5"
            }`}>
            <div className={`text-[11px] uppercase tracking-wider ${
              b.color === "orange" ? "text-orange-300/80" :
              b.color === "sky" ? "text-sky-300/80" :
              b.color === "violet" ? "text-violet-300/80" :
              "text-emerald-300/80"
            }`}>{b.label}</div>
            <div className={`mt-1 font-mono text-3xl font-bold ${
              b.color === "orange" ? "text-orange-200" :
              b.color === "sky" ? "text-sky-200" :
              b.color === "violet" ? "text-violet-200" :
              "text-emerald-200"
            }`}>{b.value}</div>
            <div className="mt-1 text-[10px] text-zinc-400" title="Retururi ÷ Comenzi eMAG (Finalizate + Returnate) × 100, pentru toate cele 6 conturi">
              {pct !== null ? (
                <>din <strong className="text-zinc-200">{b.orders}</strong> comenzi · <strong className="text-zinc-200">{pct.toFixed(2)}%</strong></>
              ) : (
                <>din {b.orders} comenzi</>
              )}
            </div>
          </div>
        );})}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          data-testid="emag-rma-send-daily"
          onClick={() => sendDigest("daily")}
          className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs font-medium text-zinc-200 hover:border-orange-500/40"
        >
          📧 Trimite digest zilnic (retururile de ieri)
        </button>
        <button
          data-testid="emag-rma-send-monthly"
          onClick={() => sendDigest("monthly")}
          className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs font-medium text-zinc-200 hover:border-orange-500/40"
        >
          📧 Trimite digest lunar (luna trecută)
        </button>
        <button
          data-testid="emag-rma-product-report"
          onClick={() => setProductReportOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-orange-500/40 bg-orange-500/10 px-3 py-1.5 text-xs font-medium text-orange-300 hover:bg-orange-500/20"
        >
          <BarChart3 className="h-3.5 w-3.5" /> Raport produse returnate
        </button>
      </div>
      {productReportOpen && <ProductReturnReportModal onClose={() => setProductReportOpen(false)} />}

      {/* Status cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {statusCards.map((c) => (
          <button key={c.k} onClick={() => { setPage(1); setStatusFilter(statusFilter === c.k ? "" : c.k); }}
            data-testid={`stat-card-${c.k}`}
            className={`text-left rounded-xl border bg-zinc-900/60 px-4 py-3 transition hover:bg-zinc-900 ${
              statusFilter === c.k ? "border-emerald-500/60 ring-1 ring-emerald-500/30" : "border-zinc-800"
            }`}>
            <div className="text-[11px] uppercase tracking-wider text-zinc-500">{c.label}</div>
            <div className="text-2xl font-bold text-zinc-100 mt-1">{c.n}</div>
          </button>
        ))}
      </div>

      {/* Platform filter */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 space-y-3">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-zinc-400">
          <Filter className="h-3.5 w-3.5" /> Platforme
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => selectGroup("BAU")} data-testid="filter-bau-toggle"
            className="rounded-md border border-sky-500/40 bg-sky-500/10 px-3 py-1 text-xs font-semibold text-sky-300 hover:bg-sky-500/20">
            BAU (toate)
          </button>
          <button onClick={() => selectGroup("FBE")} data-testid="filter-fbe-toggle"
            className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-300 hover:bg-emerald-500/20">
            FBE (toate)
          </button>
          {selectedPlatforms.size > 0 && (
            <button onClick={() => { setPage(1); setSelectedPlatforms(new Set()); }}
              className="rounded-md border border-zinc-700 bg-zinc-800 px-3 py-1 text-xs text-zinc-200 hover:border-zinc-600">
              Resetează
            </button>
          )}
          <div className="grow" />
          <select
            data-testid="rma-status-filter"
            value={statusFilter}
            onChange={(e) => { setPage(1); setStatusFilter(e.target.value); }}
            className="rounded-md border border-zinc-800 bg-zinc-900 px-2.5 py-1.5 text-xs text-zinc-100 outline-none focus:border-emerald-500/50"
          >
            <option value="">Status: toate</option>
            {Object.entries(STATUSES).map(([k, def]) => (
              <option key={k} value={k}>{def.label}</option>
            ))}
          </select>
          <div className="relative">
            <Search className="h-3.5 w-3.5 text-zinc-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input data-testid="rma-search"
              value={query}
              onChange={(e) => { setPage(1); setQuery(e.target.value); }}
              placeholder="caută după RMA / comandă / nume client"
              className="rounded-md border border-zinc-800 bg-zinc-900 pl-8 pr-3 py-1.5 text-xs text-zinc-100 w-72 outline-none focus:border-emerald-500/50" />
          </div>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {PLATFORMS.map((p) => {
            const active = selectedPlatforms.has(p.key);
            const isFbe = p.group === "FBE";
            return (
              <button key={p.key} onClick={() => togglePlatform(p.key)}
                data-testid={`platform-chip-${p.key}`}
                className={`rounded-md border px-2.5 py-1.5 text-xs font-medium transition ${
                  active
                    ? isFbe
                      ? "border-emerald-500/60 bg-emerald-500/15 text-emerald-200"
                      : "border-sky-500/60 bg-sky-500/15 text-sky-200"
                    : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200"
                }`}>
                <div className="flex items-center justify-between gap-2">
                  <span>{p.label}</span>
                  <span className="text-[10px] font-mono opacity-70">{stats.by_platform?.[p.key] || 0}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 overflow-x-auto">
        <table className="w-full text-sm min-w-[1100px]">
          <thead className="bg-zinc-900/60 text-[11px] uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="text-left px-3 py-2.5">RMA #</th>
              <th className="text-left px-3 py-2.5">Platformă</th>
              <th className="text-left px-3 py-2.5">Comandă</th>
              <th className="text-left px-3 py-2.5">Client</th>
              <th className="text-left px-3 py-2.5">Telefon</th>
              <th className="text-left px-3 py-2.5">Produs</th>
              <th className="text-right px-3 py-2.5">Qty</th>
              <th className="text-left px-3 py-2.5">Status</th>
              <th className="text-left px-3 py-2.5">Data retur</th>
              <th className="px-3 py-2.5" />
            </tr>
          </thead>
          <tbody data-testid="rma-table-body" className="divide-y divide-zinc-800">
            {loading && (
              <tr><td colSpan={10} className="text-center text-zinc-500 py-12">
                <Loader2 className="h-5 w-5 animate-spin mx-auto" />
              </td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={10} className="text-center text-zinc-500 py-12">
                <AlertCircle className="h-5 w-5 mx-auto mb-2 opacity-50" />
                Niciun RMA pentru filtrul curent.
              </td></tr>
            )}
            {!loading && items.map((r) => {
              const st = STATUSES[r.request_status] || { label: r.status_label || "—", cls: "bg-zinc-700/30 text-zinc-300 border-zinc-700/50" };
              const platLabel = PLATFORMS.find((p) => p.key === r._integration_key)?.label || r._integration_key;
              return (
                <tr key={r._composite_id} className="hover:bg-zinc-900/50" data-testid={`rma-row-${r._emag_id}`}>
                  <td className="px-3 py-2 font-mono text-zinc-200">{r.emag_id || r._emag_id}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center rounded px-2 py-0.5 text-[11px] font-medium ${
                      r._integration_key?.endsWith("_fbe") ? "bg-emerald-500/15 text-emerald-300" : "bg-sky-500/15 text-sky-300"
                    }`}>{platLabel}</span>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-zinc-300">{r.order_id || "—"}</td>
                  <td className="px-3 py-2 text-zinc-200">{r.customer_name || r.customer?.name || "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs text-zinc-300">{r.customer_phone || "—"}</td>
                  <td className="px-3 py-2 text-zinc-300 max-w-md truncate">{r._first_product_name || "—"}</td>
                  <td className="px-3 py-2 text-right text-zinc-200">{r._product_count || 0}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${st.cls}`}>
                      {st.label}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-[11px] text-zinc-400 font-mono">
                    {(r.date || "").slice(0, 10) || "—"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => setOpenRma(r)} data-testid={`rma-view-${r._emag_id}`}
                      className="inline-flex items-center gap-1 rounded border border-zinc-700 bg-zinc-800 px-2 py-1 text-xs text-zinc-200 hover:border-zinc-600">
                      <Eye className="h-3 w-3" /> Detalii
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!loading && total > PAGE_SIZE && (
          <div className="flex items-center justify-between border-t border-zinc-800 px-3 py-2 text-xs text-zinc-400">
            <div>Total: {total}</div>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                className="rounded border border-zinc-700 bg-zinc-800 px-2 py-1 disabled:opacity-40">‹</button>
              <span>Pagina {page} / {totalPages}</span>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="rounded border border-zinc-700 bg-zinc-800 px-2 py-1 disabled:opacity-40">›</button>
            </div>
          </div>
        )}
      </div>

      {openRma && (
        <RmaDetailModal rma={openRma} onClose={() => setOpenRma(null)} onUpdated={() => { setOpenRma(null); load(); }} />
      )}
    </div>
  );
}

function RmaDetailModal({ rma, onClose, onUpdated }) {
  const [draft, setDraft] = useState({
    request_status: rma.request_status,
    observations: rma.observations || "",
  });
  const [saving, setSaving] = useState(false);

  const dirty =
    draft.request_status !== rma.request_status ||
    draft.observations !== (rma.observations || "");

  // Allowed targets for the dropdown: current status + everything the
  // eMAG state-machine accepts. The current status stays selectable so the
  // user can revert without saving.
  const allowedTargets = useMemo(() => {
    const t = STATUS_TRANSITIONS[rma.request_status] || [];
    return [rma.request_status, ...t];
  }, [rma.request_status]);

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      const body = {};
      if (draft.request_status !== rma.request_status) body.request_status = draft.request_status;
      if (draft.observations !== (rma.observations || "")) body.observations = draft.observations;
      const r = await axios.post(`${API}/emag-rmas/${rma.emag_id || rma._emag_id}/save`, body);
      toast.success(`RMA #${rma.emag_id || rma._emag_id} actualizat în eMAG · ${STATUSES[r.data?.rma?.request_status]?.label || ""}`);
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const isGenius = rma.is_club === 1 || rma.is_fast === 1;
  const lockerId = rma.locker?.locker_id || rma.details?.locker_id || (rma.pickup_address || "").match(/EasyBox\s*#?(\d+)/i)?.[1];
  const lockerName = rma.locker?.locker_name || rma.details?.locker_name ||
    ((rma.pickup_address || "").includes("EasyBox") ? rma.pickup_address?.split("[")[0]?.trim() : null);
  const cur = STATUSES[rma.request_status] || { label: rma.status_label || "—", cls: "border-zinc-700 text-zinc-300 bg-zinc-800/40", dot: "bg-zinc-600" };
  const tl = rma.timeline || {};

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div data-testid="rma-detail-modal" className="w-full max-w-6xl rounded-2xl border border-zinc-800 bg-zinc-950 shadow-2xl max-h-[94vh] overflow-hidden flex flex-col">
        {/* Header bar — title left, action buttons right (mirrors eMAG dashboard) */}
        <div className="flex items-center justify-between border-b border-zinc-800 px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-zinc-50">
              Detalii retur · <span className="text-orange-300 font-mono">#{rma.emag_id || rma._emag_id}</span>
            </h2>
            <p className="text-xs text-zinc-500 mt-0.5">
              din data <span className="text-zinc-300">{rma.date || "—"}</span> · {rma._integration_name || rma._integration_key?.toUpperCase()}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={save} disabled={!dirty || saving} data-testid="rma-save-btn"
              className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold transition ${
                dirty
                  ? "bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50"
                  : "border border-zinc-700 bg-zinc-800 text-zinc-500 cursor-not-allowed"
              }`}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Salvează modificările
            </button>
            <button onClick={onClose} data-testid="rma-detail-close"
              className="inline-flex items-center gap-1.5 rounded-md border border-rose-500/40 bg-rose-500/5 px-3 py-2 text-sm font-medium text-rose-300 hover:bg-rose-500/15">
              <X className="h-4 w-4" /> Anulează
            </button>
          </div>
        </div>

        <div className="overflow-y-auto px-6 py-5 space-y-6 flex-1 text-sm">
          {/* === Top: 3 columns (Cerere · Client · Pickup) === */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* === Informații cerere === */}
            <Section title="Informații cerere">
              <Field label="ID comandă">
                <span className="font-mono text-zinc-200">{rma.order_id || "—"}</span>
              </Field>
              <Field label="Status retur">
                <div className="flex items-center gap-2">
                  {/* Live preview swatch reflects the currently selected option */}
                  <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${STATUSES[draft.request_status]?.dot || "bg-zinc-500"}`} />
                  <select data-testid="rma-status-select"
                    value={draft.request_status}
                    onChange={(e) => setDraft({ ...draft, request_status: Number(e.target.value) })}
                    className="flex-1 rounded-md border border-zinc-800 bg-zinc-900 px-2.5 py-1.5 text-sm text-zinc-100 outline-none focus:border-emerald-500/50">
                    {allowedTargets.map((s) => (
                      <option key={s} value={s}>{STATUSES[s]?.label || s}</option>
                    ))}
                  </select>
                </div>
                {draft.request_status !== rma.request_status && (
                  <p className="text-[10px] text-amber-300 mt-1">Apasă &quot;Salvează modificările&quot; ca să trimiți la eMAG.</p>
                )}
              </Field>
              <Field label="Tip retur">
                <span className="text-zinc-200">{rma.return_type_label || RETURN_TYPES[rma.return_type] || "—"}</span>
              </Field>
              {(rma.return_reason != null) && (
                <Field label="Motiv principal retur">
                  <div className="rounded-md border border-orange-500/40 bg-orange-500/10 px-2.5 py-1.5">
                    <p className="text-sm font-semibold text-orange-200">
                      {rma.return_reason_label || `Cod ${rma.return_reason}`}
                    </p>
                    <p className="text-[10px] font-mono text-orange-400/60 mt-0.5">cod #{rma.return_reason}</p>
                  </div>
                </Field>
              )}
              <Field label="Taxă returnare (cu TVA)">
                <span className="font-mono text-zinc-200">{Number(rma.return_tax_value || 0).toFixed(2)} {rma.currency || "RON"}</span>
              </Field>
              <Field label="Adresă retur (depozit)">
                <span className="text-zinc-300 text-xs">{rma.return_address_snapshot || "—"}</span>
              </Field>
            </Section>

            {/* === Informații client === */}
            <Section title="Informații client" right={isGenius && (
              <span className="rounded-md bg-violet-500/15 text-violet-300 border border-violet-500/40 px-2 py-0.5 text-[10px] font-semibold">Genius</span>
            )}>
              <Field label="Nume">{rma.customer_name || "—"}</Field>
              <Field label="Telefon">
                <span className="font-mono">{rma.customer_phone || "—"}</span>
              </Field>
              <Field label="Companie">{rma.customer_company || "—"}</Field>
              <div className="border-t border-zinc-800 pt-2 mt-2">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Restituire</p>
                <Field label="IBAN">
                  <span className="font-mono text-xs">{rma.customer_account_iban || "—"}</span>
                </Field>
                <Field label="Bancă">{rma.customer_account_bank || "—"}</Field>
                <Field label="Beneficiar">{rma.customer_account_beneficiary || "—"}</Field>
              </div>
            </Section>

            {/* === Informații pickup === */}
            <Section title="Informații pickup">
              <Field label="Metodă pickup">
                <span className="text-zinc-200">{rma.pickup_method_label || PICKUP_METHODS[rma.pickup_method] || "—"}</span>
              </Field>
              {(lockerId || lockerName) && (
                <Field label="Locker">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-zinc-300 text-xs">{lockerName || "—"}</span>
                    {lockerId && <span className="font-mono text-xs text-orange-300">#{lockerId}</span>}
                  </div>
                </Field>
              )}
              <Field label="Adresă pickup">
                <span className="text-zinc-300 text-xs">{rma.pickup_address || "—"}</span>
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Județ">{rma.pickup_suburb || "—"}</Field>
                <Field label="Oraș">{rma.pickup_city || "—"}</Field>
              </div>
              <Field label="Cod poștal">
                <span className="font-mono">{rma.pickup_zipcode || "—"}</span>
              </Field>
            </Section>
          </div>

          {/* === Timeline (5 milestones) === */}
          {tl.data_retur && (
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-3">Cronologie</p>
              <div className="flex items-center justify-between gap-2 overflow-x-auto">
                {[
                  { n: 1, label: "Data retur",        date: tl.data_retur,             done: true },
                  { n: 2, label: "Estimat ridicare",  date: tl.estimat_ridicare,       done: rma.request_status >= 3 },
                  { n: 3, label: "Estimat recepție",  date: tl.estimat_receptionare,   done: rma.request_status >= 6 },
                  { n: 4, label: "Max. finalizare",   date: tl.data_maxima_finalizare, done: rma.request_status >= 7 },
                  { n: 5, label: "Finalizare incident", date: tl.finalizare_cu_incident, done: false, danger: true },
                ].map((step, i, arr) => (
                  <React.Fragment key={step.n}>
                    <div className="flex flex-col items-center gap-1 min-w-[110px]">
                      <div className={`h-8 w-8 rounded-full border-2 flex items-center justify-center text-xs font-bold ${
                        step.done ? "border-emerald-500 bg-emerald-500/10 text-emerald-300"
                        : step.danger ? "border-amber-500/60 text-amber-400 border-dashed"
                        : "border-zinc-700 text-zinc-500"
                      }`}>{step.n}</div>
                      <p className={`text-[10px] font-medium uppercase tracking-wider ${step.danger ? "text-amber-400" : "text-zinc-400"}`}>{step.label}</p>
                      <p className={`text-[11px] font-mono ${step.done ? "text-zinc-200" : "text-zinc-500"}`}>{step.date || "—"}</p>
                    </div>
                    {i < arr.length - 1 && (
                      <div className={`h-0.5 flex-1 min-w-[20px] ${step.done ? "bg-emerald-500/40" : "bg-zinc-800"}`} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}

          {/* === Lista produse === */}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Listă produse</p>
            <div className="overflow-x-auto rounded-xl border border-zinc-800">
              <table className="w-full text-sm" data-testid="rma-products-table">
                <thead className="bg-zinc-900/60 text-[11px] uppercase tracking-wider text-zinc-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Produs</th>
                    <th className="px-3 py-2 text-right">Cant.</th>
                    <th className="px-3 py-2 text-right">Val. reținută</th>
                    <th className="px-3 py-2 text-left">Motiv specific (sec.)</th>
                    <th className="px-3 py-2 text-left">Motiv refuz</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800">
                  {(rma.products || []).length === 0 && (
                    <tr><td colSpan={5} className="px-3 py-6 text-center text-zinc-500">Fără produse listate.</td></tr>
                  )}
                  {(rma.products || []).map((p, i) => {
                    const secondaryLabel = p.return_reason_label;
                    const secondaryCode  = p.return_reason;
                    const rejectLabel = p.reject_reason_label;
                    return (
                      <tr key={i} className="text-zinc-200 align-top">
                        <td className="px-3 py-2 max-w-sm">
                          <p className="font-mono text-[10px] text-zinc-500 mb-0.5">ID #{p.product_id || p.id || p.product_emag_id || "—"}</p>
                          <p className="text-zinc-100 text-sm">{p.product_name || p.name || "—"}</p>
                          {p.part_number && (
                            <p className="text-[11px] font-mono text-zinc-500 mt-0.5">SKU: {p.part_number}</p>
                          )}
                          {p.diagnostic && (
                            <p className="text-[11px] text-zinc-400 mt-0.5">Diagnostic: <span className="text-zinc-300">{p.diagnostic}</span></p>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-mono">{p.quantity ?? "—"}</td>
                        <td className="px-3 py-2 text-right font-mono">{Number(p.retained_amount || 0).toFixed(2)} <span className="text-zinc-500 text-[10px]">{rma.currency || "RON"}</span></td>
                        <td className="px-3 py-2 text-xs">
                          {secondaryCode != null ? (
                            <div className="rounded-md border border-sky-500/30 bg-sky-500/5 px-2 py-1">
                              <p className="text-sky-200 text-[12px] leading-snug">{secondaryLabel || `Cod ${secondaryCode}`}</p>
                              <p className="text-[10px] font-mono text-sky-400/60 mt-0.5">cod #{secondaryCode}</p>
                            </div>
                          ) : <span className="text-zinc-600">—</span>}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {p.reject_reason != null ? (
                            <div className="rounded-md border border-rose-500/30 bg-rose-500/5 px-2 py-1">
                              <p className="text-rose-200 text-[12px] leading-snug">{rejectLabel || `Cod ${p.reject_reason}`}</p>
                              <p className="text-[10px] font-mono text-rose-400/60 mt-0.5">cod #{p.reject_reason}</p>
                            </div>
                          ) : <span className="text-zinc-600">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* === Observații seller (notes pushed to eMAG together with status) === */}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Observații seller (vizibile pentru cumpărător)</p>
            <textarea data-testid="rma-observations" rows={3}
              value={draft.observations}
              onChange={(e) => setDraft({ ...draft, observations: e.target.value })}
              placeholder="Notițe care vor fi trimise împreună cu noul status către eMAG"
              className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-emerald-500/50" />
          </div>

          {/* === AWB section === */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500">AWB</p>
              {(rma.awbs || []).some(a => !a.awb_number) && (
                <button onClick={async () => {
                  try {
                    const r = await axios.post(`${API}/emag-rmas/${rma._composite_id}/refresh-awb`);
                    if (r.data.updated > 0) toast.success(`${r.data.updated} AWB-uri actualizate din eMAG`);
                    else toast.info("eMAG nu expune încă numărul AWB pentru această rezervare.");
                    onUpdated();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || e.message);
                  }
                }} data-testid="rma-awb-refresh"
                  className="inline-flex items-center gap-1.5 rounded-md border border-sky-500/40 bg-sky-500/10 px-2.5 py-1 text-[11px] font-medium text-sky-200 hover:bg-sky-500/20">
                  <RefreshCw className="h-3 w-3" /> Reîmprospătează AWB din eMAG
                </button>
              )}
            </div>
            <div className="overflow-x-auto rounded-xl border border-zinc-800">
              <table className="w-full text-sm" data-testid="rma-awb-table">
                <thead className="bg-zinc-900/60 text-[11px] uppercase tracking-wider text-zinc-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Tip</th>
                    <th className="px-3 py-2 text-left">Număr</th>
                    <th className="px-3 py-2 text-left">Colet</th>
                    <th className="px-3 py-2 text-left">Curier</th>
                    <th className="px-3 py-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800">
                  {(!rma.awbs || rma.awbs.length === 0) && (
                    <tr><td colSpan={5} className="px-3 py-6 text-center text-zinc-500">Niciun AWB / rezervare asociată.</td></tr>
                  )}
                  {(rma.awbs || []).map((a, i) => {
                    const hasAwb = !!a.awb_number;
                    const stCode = a.awb_status_code;
                    const stName = a.awb_status_name;
                    const stCls = stCode === "NEW" ? "bg-amber-500/15 text-amber-300 border-amber-500/40"
                                : stCode === "IN_TRANSIT" || stCode === "PICKUP" ? "bg-sky-500/15 text-sky-300 border-sky-500/40"
                                : stCode === "DELIVERED" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
                                : stCode === "CANCELED" || stCode === "RETURNED" ? "bg-rose-500/15 text-rose-300 border-rose-500/40"
                                : "bg-zinc-500/15 text-zinc-300 border-zinc-500/40";
                    return (
                      <tr key={i} className="text-zinc-200">
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center rounded px-2 py-0.5 text-[11px] font-medium ${
                            hasAwb ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40"
                                   : "bg-amber-500/15 text-amber-300 border border-amber-500/40"
                          }`}>{hasAwb ? "AWB" : "Rezervare eMAG"}</span>
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-orange-300">{a.awb_number || a.reservation_id || "—"}</td>
                        <td className="px-3 py-2 font-mono text-xs text-zinc-400">{a.awb_barcode || a.parcel_number || a.colet_id || "—"}</td>
                        <td className="px-3 py-2 text-zinc-300 text-xs">{a.courier_name || a.delivery_type || rma.pickup_method_label || "—"}</td>
                        <td className="px-3 py-2">
                          {stName ? (
                            <span className={`inline-flex items-center rounded border px-2 py-0.5 text-[11px] font-medium ${stCls}`}>{stName === "New" ? "AWB creat" : stName}</span>
                          ) : <span className="text-zinc-600 text-xs">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {(rma.awbs || []).some(a => !a.awb_number) && (
              <p className="text-[11px] text-zinc-500 mt-2">
                <span className="text-amber-400">●</span> Pentru această rezervare AWB-ul nu a fost încă generat de curier. Apasă &quot;Reîmprospătează AWB&quot; — sistemul interoghează endpoint-ul eMAG <span className="font-mono text-zinc-400">awb/read</span> și aduce numărul real (ex. <span className="font-mono text-amber-300">1ONBLR507720134</span>) imediat ce devine disponibil.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, right, children }) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-orange-300">{title}</h3>
        {right}
      </div>
      <div className="space-y-2.5">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
      <div className="text-sm text-zinc-200">{children}</div>
    </div>
  );
}


// ─── „Raport produse returnate" Modal ─────────────────────────────
// Filter by month | year | custom range → aggregated list of SKUs sorted
// DESC by return qty. User request 2026-08-21.
function ProductReturnReportModal({ onClose }) {
  const now = new Date();
  const [mode, setMode] = useState("month"); // month | year | custom
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [customFrom, setCustomFrom] = useState(new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10));
  const [customTo, setCustomTo] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");

  const MONTHS = ["Ianuarie","Februarie","Martie","Aprilie","Mai","Iunie","Iulie","August","Septembrie","Octombrie","Noiembrie","Decembrie"];

  const { dateFrom, dateTo, label } = useMemo(() => {
    if (mode === "year") return { dateFrom: `${year}-01-01`, dateTo: `${year+1}-01-01`, label: `Anul ${year}` };
    if (mode === "custom") return { dateFrom: customFrom, dateTo: customTo, label: `${customFrom} → ${customTo}` };
    const nextY = month === 12 ? year+1 : year;
    const nextM = month === 12 ? 1 : month+1;
    return { dateFrom: `${year}-${String(month).padStart(2,"0")}-01`, dateTo: `${nextY}-${String(nextM).padStart(2,"0")}-01`, label: `${MONTHS[month-1]} ${year}` };
  }, [mode, year, month, customFrom, customTo]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/emag-rmas/product-report?date_from=${dateFrom}&date_to=${dateTo}`);
      setData(r.data);
    } catch (e) {
      toast.error("Eroare: " + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!data?.items) return [];
    if (!q) return data.items;
    return data.items.filter(r => (r.sku || "").toLowerCase().includes(q) || (r.name || "").toLowerCase().includes(q));
  }, [data, query]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="relative w-full max-w-4xl overflow-hidden rounded-xl border border-zinc-800 bg-zinc-950 shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-zinc-800 bg-gradient-to-r from-orange-500/10 to-transparent p-4">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-orange-400" />
            <div>
              <h3 className="text-base font-bold text-zinc-100">Raport produse returnate</h3>
              <p className="text-[11px] text-zinc-500">Agregat pe SKU · sortat descrescător (cele mai returnate primele)</p>
            </div>
          </div>
          <button onClick={onClose} className="rounded-md p-1 text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200" data-testid="close-report">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-2 border-b border-zinc-800 bg-zinc-900/40 p-3">
          <div className="flex items-center gap-1 rounded-md border border-zinc-800 bg-zinc-900 p-0.5">
            {[
              { id: "month", label: "Lună" },
              { id: "year", label: "An" },
              { id: "custom", label: "Perioadă" },
            ].map((b) => (
              <button key={b.id} onClick={() => setMode(b.id)}
                className={`rounded px-3 py-1 text-xs font-medium transition-colors ${
                  mode === b.id ? "bg-orange-600 text-white" : "text-zinc-400 hover:text-zinc-200"
                }`}
                data-testid={`report-range-${b.id}`}>{b.label}</button>
            ))}
          </div>
          {mode === "month" && (
            <div className="flex items-center gap-2">
              <select value={month} onChange={e => setMonth(Number(e.target.value))}
                className="rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-xs text-zinc-100"
                data-testid="report-month">
                {MONTHS.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
              </select>
              <input type="number" value={year} onChange={e => setYear(Number(e.target.value))}
                className="w-20 rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-xs text-zinc-100"
                data-testid="report-year" />
            </div>
          )}
          {mode === "year" && (
            <input type="number" value={year} onChange={e => setYear(Number(e.target.value))}
              className="w-24 rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-xs text-zinc-100"
              data-testid="report-year" />
          )}
          {mode === "custom" && (
            <div className="flex items-center gap-2">
              <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                className="rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-xs text-zinc-100" data-testid="report-from" />
              <span className="text-xs text-zinc-500">→</span>
              <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                className="rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-xs text-zinc-100" data-testid="report-to" />
            </div>
          )}
          <div className="relative ml-auto">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-zinc-500" />
            <input type="text" placeholder="Caută SKU / nume..." value={query} onChange={e => setQuery(e.target.value)}
              className="rounded-md border border-zinc-800 bg-zinc-900 px-7 py-1 text-xs text-zinc-100 focus:border-zinc-600 focus:outline-none w-48"
              data-testid="report-search" />
          </div>
        </div>

        {/* Summary line */}
        {data && !loading && (
          <div className="flex items-center gap-4 border-b border-zinc-800 bg-zinc-900/20 px-4 py-2 text-xs text-zinc-400">
            <span>Perioadă: <b className="text-zinc-200">{label}</b></span>
            <span>·</span>
            <span>Total retururi: <b className="text-orange-300">{data.total_rmas}</b></span>
            <span>·</span>
            <span>Buc returnate: <b className="text-rose-300">{data.total_products_returned}</b></span>
            <span>·</span>
            <span>SKU-uri unice: <b className="text-zinc-200">{data.unique_skus}</b></span>
          </div>
        )}

        {/* Table */}
        <div className="max-h-[60vh] overflow-auto">
          {loading ? (
            <div className="p-12 text-center text-sm text-zinc-500">
              <Loader2 className="mx-auto h-6 w-6 animate-spin text-orange-400" />
              <div className="mt-2">Se calculează...</div>
            </div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center text-sm text-zinc-500">
              <Package className="mx-auto h-8 w-8 text-zinc-700" />
              <div className="mt-2">Nicio retur în perioada selectată.</div>
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-zinc-900">
                <tr>
                  <th className="px-3 py-2 text-left font-medium uppercase tracking-wide text-zinc-500 w-10">#</th>
                  <th className="px-3 py-2 text-left font-medium uppercase tracking-wide text-zinc-500">Nume produs</th>
                  <th className="px-3 py-2 text-left font-medium uppercase tracking-wide text-zinc-500 w-28">SKU</th>
                  <th className="px-3 py-2 text-right font-medium uppercase tracking-wide text-zinc-500 w-24">Buc returnate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-900">
                {rows.map((r, i) => {
                  const isTop3 = i < 3;
                  return (
                    <tr key={`${r.sku}-${i}`} className={`hover:bg-zinc-900/40 ${isTop3 ? "bg-rose-500/5" : ""}`}>
                      <td className={`px-3 py-2 font-mono font-semibold ${isTop3 ? "text-rose-300" : "text-zinc-500"}`}>{i+1}</td>
                      <td className="px-3 py-2 text-zinc-200">{r.name || "—"}</td>
                      <td className="px-3 py-2 font-mono text-zinc-300">{r.sku}</td>
                      <td className={`px-3 py-2 text-right font-mono text-sm font-bold ${isTop3 ? "text-rose-300" : "text-zinc-200"}`}>{r.qty}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
